import { DailyNutrition, FoodItem, MealCategoryInput } from './nutrition'

// Parser error types
export class NutritionParseError extends Error {
  constructor(
    message: string,
    public readonly lineNumber?: number,
    public readonly rawLine?: string
  ) {
    super(message)
    this.name = 'NutritionParseError'
  }
}

export class InvalidDateError extends NutritionParseError {
  constructor(dateString: string, lineNumber?: number) {
    super(`Invalid date format: ${dateString}`, lineNumber, dateString)
    this.name = 'InvalidDateError'
  }
}

export class InvalidFoodItemError extends NutritionParseError {
  constructor(foodLine: string, lineNumber?: number, details?: string) {
    super(`Invalid food item: ${details ?? 'Unknown error'}`, lineNumber, foodLine)
    this.name = 'InvalidFoodItemError'
  }
}

export class InvalidNutritionalValueError extends NutritionParseError {
  constructor(value: string, fieldName: string, lineNumber?: number) {
    super(`Invalid nutritional value "${value}" for field "${fieldName}"`, lineNumber, value)
    this.name = 'InvalidNutritionalValueError'
  }
}

export class MissingTotalsError extends NutritionParseError {
  constructor(lineNumber?: number) {
    super('Missing TOTALS row in nutrition data', lineNumber)
    this.name = 'MissingTotalsError'
  }
}

// Parser configuration
export interface ParserConfig {
  readonly strictMode: boolean // Whether to throw on non-critical errors
  readonly allowPartialData: boolean // Whether to continue parsing if some items fail
  readonly validateTotals: boolean // Whether to validate calculated vs provided totals
  readonly tolerancePercent: number // Tolerance for totals validation (0-100)
}

export const defaultParserConfig: ParserConfig = {
  strictMode: true,
  allowPartialData: false,
  validateTotals: true,
  tolerancePercent: 1.0, // 1% tolerance for rounding errors
}

// Parse result types
export interface ParseSuccess {
  readonly success: true
  readonly data: DailyNutrition
  readonly warnings: readonly string[]
}

export interface ParseFailure {
  readonly success: false
  readonly error: NutritionParseError
  readonly partialData?: Partial<DailyNutrition>
}

export type ParseResult = ParseSuccess | ParseFailure

// Multi-day parse result types
export interface MultiDayParseSuccess {
  readonly success: true
  readonly data: readonly DailyNutrition[]
  readonly warnings: readonly string[]
}

export interface MultiDayParseFailure {
  readonly success: false
  readonly error: NutritionParseError
  readonly partialData?: readonly DailyNutrition[]
}

export type MultiDayParseResult = MultiDayParseSuccess | MultiDayParseFailure

// Intermediate parsing structures
export interface ParsedMeal {
  readonly category: MealCategoryInput
  readonly foodItems: readonly FoodItem[]
}

export interface ParseContext {
  readonly lines: readonly string[]
  readonly currentLineIndex: number
  readonly config: ParserConfig
}

// Parser interface
export interface INutritionParser {
  parse(rawData: string, config?: Partial<ParserConfig>): ParseResult
  parseDate(dateString: string): Date
  parseFoodItem(foodLine: string, nutritionValues: readonly string[]): FoodItem
  parseMeal(category: MealCategoryInput, foodLines: readonly string[]): ParsedMeal
}
